import { Alert, Breadcrumbs, Group, Paper, Stack, Text } from '@mantine/core';
import { HiOutlineArrowLeft } from 'react-icons/hi2';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import useRetrieveEditorCompositionCapabilities from '@/hooks/api/automations/useRetrieveEditorCompositionCapabilities.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import StepsTree from '@/pages/Editor/components/tree/StepsTree.tsx';
import AddStepButton from '@/pages/Editor/components/AddStepButton/AddStepButton.tsx';
import { AutomationStep } from '@/types/automationSteps.ts';

/** Reuses the editor canvas while making the selected local-module scope explicit. */
const LocalModuleEditor = () => {
  const { data: version } = useRetrieveLatestAutomationVersion();
  const { target, setActiveCompositionTarget } = useActiveCompositionTarget();
  const subflowId = target.scope_kind === 'local_subflow' ? target.subflow_id : undefined;
  const subflow = version?.local_subflows?.find((candidate) => candidate.subflow_id === subflowId);
  const steps = (subflow?.steps ?? []) as AutomationStep[];
  const { data: capabilities, isLoading: capabilitiesLoading } = useRetrieveEditorCompositionCapabilities(subflowId);
  const canCreateStep = capabilities?.supported_operations.includes('create_local_subflow_step') ?? false;

  if (!subflow) {
    return (
      <Alert color="yellow" title="Module not found">
        This module is no longer part of the latest draft. Return to the workflow to choose another module.
      </Alert>
    );
  }

  return (
    <Stack w="100%" gap="sm">
      <Breadcrumbs separator="›">
        <Text size="sm" c="dimmed">Workflow</Text>
        <Text size="sm" fw={600}>Modules</Text>
        <Text size="sm" fw={600}>{subflow.name}</Text>
      </Breadcrumbs>
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Text fw={700} size="xl">{subflow.name}</Text>
          <Text size="sm" c="dimmed">
            Private to this automation version. It has no trigger or independent lifecycle.
          </Text>
        </Stack>
        <StyledButton
          size="sm"
          variant="outline"
          leftSection={<HiOutlineArrowLeft size={15} />}
          onClick={() => void setActiveCompositionTarget({ scope_kind: 'root' })}
        >
          Back to workflow
        </StyledButton>
      </Group>
      {steps.length === 0 ? (
        <Paper withBorder p="xl" mt="xl">
          <Stack align="center" gap="sm">
            <Text fw={600}>Build this module</Text>
            <Text size="sm" c="dimmed" ta="center">
              Start with the first internal step. You can add typed inputs, outputs, and workflow callers when the flow takes shape.
            </Text>
            {capabilitiesLoading ? (
              <Text size="sm" c="dimmed">Loading module editing permissions…</Text>
            ) : canCreateStep ? (
              <AddStepButton
                addSiblingParents={[{ parentStep: undefined, insertIndex: 0 }]}
                alwaysShow
                buttonLabel="Add first step"
              />
            ) : (
              <Alert color="gray">
                {capabilities?.read_only_reason ?? 'This module is available for review only.'}
              </Alert>
            )}
          </Stack>
        </Paper>
      ) : (
        <StepsTree
          steps={steps}
          indentationWidth={40}
          showRootAddButton
          canAdd={canCreateStep}
          canReorder={capabilities?.supported_operations.includes('move_local_subflow_step') ?? false}
        />
      )}
    </Stack>
  );
};

export default LocalModuleEditor;
